
import {
  cellKey,
  parseCellKey,
  type CellKey,
  type CellState,
} from '@client/shared/calendar/slot-grid';
import { cellStartInstant } from './booking-grid.ts';

export interface SlotWindow {
  readonly startSlot: number;
  readonly endSlot: number;
}

export interface EditTimes {
  readonly start: Date;
  readonly end: Date;
}

export interface EditBounds {
  readonly canEarlierStart: boolean;
  readonly canLaterStart: boolean;
  readonly canEarlierEnd: boolean;
  readonly canLaterEnd: boolean;
}

export interface EditableSpan {
  readonly times: EditTimes;
  readonly bounds: EditBounds;
  nudge(edge: 'start' | 'end', delta: -1 | 1): ReadonlySet<CellKey> | null;
}

interface SpanSlots {
  readonly dayIndex: number;
  readonly startSlot: number;
  readonly endSlot: number;
}

function spanSlots(painted: ReadonlySet<CellKey>): SpanSlots | null {
  if (painted.size === 0) return null;
  let dayIndex = -1;
  let minSlot = Number.POSITIVE_INFINITY;
  let maxSlot = Number.NEGATIVE_INFINITY;
  for (const key of painted) {
    const { day, slot } = parseCellKey(key);
    if (dayIndex !== -1 && day !== dayIndex) return null;
    dayIndex = day;
    if (slot < minSlot) minSlot = slot;
    if (slot > maxSlot) maxSlot = slot;
  }
  if (maxSlot - minSlot + 1 !== painted.size) return null;
  return { dayIndex, startSlot: minSlot, endSlot: maxSlot + 1 };
}

function paintRun(dayIndex: number, startSlot: number, endSlot: number): Set<CellKey> {
  const cells = new Set<CellKey>();
  for (let slot = startSlot; slot < endSlot; slot++) cells.add(cellKey(dayIndex, slot));
  return cells;
}

export function editableSpan(
  painted: ReadonlySet<CellKey>,
  days: readonly string[],
  zone: string,
  cellState: (day: number, slot: number) => CellState,
  window: SlotWindow,
): EditableSpan | null {
  const slots = spanSlots(painted);
  if (!slots) return null;
  const date = days[slots.dayIndex];
  if (!date) return null;
  const { dayIndex, startSlot, endSlot } = slots;

  const freeNeighbour = (slot: number) =>
    slot >= window.startSlot && slot < window.endSlot && cellState(dayIndex, slot) === 'free';

  const times: EditTimes = {
    start: cellStartInstant(date, startSlot, zone),
    end: cellStartInstant(date, endSlot, zone),
  };

  const bounds: EditBounds = {
    canEarlierStart: startSlot > window.startSlot && freeNeighbour(startSlot - 1),
    canLaterStart: endSlot - startSlot > 1,
    canEarlierEnd: endSlot - startSlot > 1,
    canLaterEnd: endSlot < window.endSlot && freeNeighbour(endSlot),
  };

  const nudge = (edge: 'start' | 'end', delta: -1 | 1): ReadonlySet<CellKey> | null => {
    let nextStart = startSlot;
    let nextEnd = endSlot;
    if (edge === 'start') nextStart += delta;
    else nextEnd += delta;
    if (nextStart < window.startSlot || nextEnd > window.endSlot) return null;
    if (nextEnd - nextStart < 1) return null;
    for (let slot = nextStart; slot < nextEnd; slot++) {
      if (cellState(dayIndex, slot) !== 'free') return null;
    }
    return paintRun(dayIndex, nextStart, nextEnd);
  };

  return { times, bounds, nudge };
}
